import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle, Loader } from 'lucide-react';
const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const {
    login
  } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.email.trim() || !formData.password) {
      setError('Please enter both email and password');
      return;
    }
    setIsSubmitting(true);
    try {
      await login(formData.email, formData.password, formData.rememberMe);
      navigate('/dashboard'); // Redirect to dashboard after successful login
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 max-w-md mx-auto" data-id="element-95">
      <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-96">Welcome Back</h2>
      {error && <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6 flex items-center" data-id="element-97">
          <AlertCircle size={20} className="mr-2" data-id="element-98" />
          <span data-id="element-99">{error}</span>
        </div>}
      <form onSubmit={handleSubmit} data-id="element-100">
        <div className="mb-6" data-id="element-101">
          <label htmlFor="email" className="block text-navy-900 font-medium mb-2" data-id="element-102">
            Email or Phone Number
          </label>
          <input type="text" id="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" placeholder="Enter your email or phone number" data-id="element-103" />
        </div>
        <div className="mb-6" data-id="element-104">
          <div className="flex justify-between mb-2" data-id="element-105">
            <label htmlFor="password" className="block text-navy-900 font-medium" data-id="element-106">
              Password
            </label>
            <Link to="/auth/forgot-password" className="text-gold-500 hover:underline text-sm" data-id="element-107">
              Forgot Password?
            </Link>
          </div>
          <input type="password" id="password" name="password" value={formData.password} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" placeholder="Enter your password" data-id="element-108" />
        </div>
        <div className="mb-6" data-id="element-109">
          <label className="flex items-center" data-id="element-110">
            <input type="checkbox" name="rememberMe" checked={formData.rememberMe} onChange={handleChange} className="mr-2" data-id="element-111" />
            <span className="text-navy-700" data-id="element-112">Remember me</span>
          </label>
        </div>
        <button type="submit" disabled={isSubmitting} className="w-full bg-navy-900 hover:bg-navy-800 text-white py-3 rounded-md font-medium transition-colors flex items-center justify-center" data-id="element-113">
          {isSubmitting ? <>
              <Loader size={20} className="animate-spin mr-2" data-id="element-114" />
              Logging In...
            </> : 'Log In'}
        </button>
      </form>
      <div className="mt-6" data-id="element-115">
        <div className="relative flex items-center justify-center" data-id="element-116">
          <div className="border-t border-gray-300 flex-grow" data-id="element-117"></div>
          <span className="mx-4 text-gray-500 text-sm" data-id="element-118">OR</span>
          <div className="border-t border-gray-300 flex-grow" data-id="element-119"></div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4" data-id="element-120">
          <button className="flex items-center justify-center bg-white border border-gray-300 rounded-md py-2 px-4 hover:bg-gray-50 transition-colors" data-id="element-121">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5 mr-2" data-id="element-122" />
            <span className="text-sm" data-id="element-123">Google</span>
          </button>
          <button className="flex items-center justify-center bg-[#1877F2] text-white rounded-md py-2 px-4 hover:bg-[#166FE5] transition-colors" data-id="element-124">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="mr-2" data-id="element-125">
              <path d="M24 12.073c0-5.8-4.2-10.449-9.601-11.473v8.399h2.799l.499 3h-3.298v2.901c0 1.1.399 2.099 1.801 2.099h1.5v3c-.3 0-1.5.1-2.8.1-2.5 0-4.399-1.599-4.399-4.3v-3.8h-3v-3h3v-8.4c-5.5 1-9.5 5.7-9.5 11.5 0 6.351 5.149 11.5 11.5 11.5s11.5-5.149 11.5-11.5z" data-id="element-126" />
            </svg>
            <span className="text-sm" data-id="element-127">Facebook</span>
          </button>
        </div>
      </div>
      <div className="mt-6 text-center" data-id="element-128">
        <p className="text-navy-700" data-id="element-129">
          Don't have an account?{' '}
          <Link to="/auth/register" className="text-gold-500 hover:underline font-medium" data-id="element-130">
            Register
          </Link>
        </p>
      </div>
    </div>;
};
export default LoginForm;